import { Content } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import cx from 'classnames';
import { Text } from 'components/@tedui';
import Heading from 'components/PRISMIC/Blog/Heading';
import PrismicConditionalLink from 'components/PRISMIC/PrismicConditionalLink';
import BaseButton, { BaseButtonType } from 'components/base-button';
import ContentContainer from 'components/content-container';
import type { LINK_TYPE } from 'components/pages/homepage/HeroSlider';
import mixpanel from 'lib/analytics/mixpanel';
import { PrismicNextImage } from '../../PrismicNextImage';

import type { JSX } from 'react';

/**
 * Props for `MembershipSpotlight`.
 */
export type MembershipSpotlightProps = {
  context: { mixpanelTitle?: string };
} & SliceComponentProps<Content.MembershipSpotlightSlice>;

/**
 * Component for "MembershipSpotlight" Slices.
 */
const MembershipSpotlight = ({
  slice,
  context
}: MembershipSpotlightProps): JSX.Element => {
  const hiddenClassName = cx({
    'invisible h-0': slice.primary.is_hidden
  });

  const isDarkTheme = slice.primary.is_dark_theme;

  const containerClasses = cx('mx-auto flex flex-row px-5 pb-12 lg-tui:px-6', {
    'm-0 bg-black': isDarkTheme
  });

  const textClasses = cx({
    'text-white': isDarkTheme
  });

  const trackClick = (linkType: LINK_TYPE) => {
    mixpanel.track('homepage_click', {
      current_url: window.location.href,
      title: context.mixpanelTitle,
      item_number: '',
      link_type: linkType,
      value: ''
    });
  };

  return (
    <div
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={hiddenClassName}
    >
      <ContentContainer className={containerClasses} noPadding>
        <div className="flex flex-col gap-6 pt-12 sm:flex-col-reverse md:flex-col-reverse lg:flex-row">
          <div className="flex flex-col sm:w-full md:w-full lg:w-1/3">
            <PrismicConditionalLink
              data-slice-type={slice.slice_type}
              data-slice-variation={slice.variation}
              field={slice.primary.call_to_action_link_title}
              onClick={() => trackClick('title')}
            >
              <Heading
                variant="h3"
                className={`mb-2 ${textClasses}`}
                weight="semi-bold"
              >
                {slice.primary.title}
              </Heading>
            </PrismicConditionalLink>
            <Text
              useNewTextStyles
              variant="body1"
              tag="span"
              UNSAFE_className={textClasses}
            >
              <PrismicRichText field={slice.primary.text_content} />
            </Text>
            <div className="mt-8">
              <PrismicConditionalLink
                data-slice-type={slice.slice_type}
                data-slice-variation={slice.variation}
                field={slice.primary.call_to_action_link}
                onClick={() => trackClick('button')}
              >
                <BaseButton
                  isDarkBackground={isDarkTheme}
                  text={slice.primary.call_to_action_text}
                  variant={BaseButtonType.Secondary}
                />
              </PrismicConditionalLink>
            </div>
          </div>
          {slice.primary.membership_image && (
            <div className="h-100 sm:w-100 overflow-hidden sm:w-full md:w-full lg:w-2/3">
              <PrismicConditionalLink
                data-slice-type={slice.slice_type}
                data-slice-variation={slice.variation}
                field={slice.primary.call_to_action_link_image}
                onClick={() => trackClick('image')}
              >
                <PrismicNextImage
                  field={slice.primary.membership_image}
                  style={{ objectFit: 'cover' }}
                  alt={slice.primary.membership_image.alt || ''}
                />
              </PrismicConditionalLink>
            </div>
          )}
        </div>
      </ContentContainer>
    </div>
  );
};

export default MembershipSpotlight;
